import React from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams, useNavigate } from 'react-router-dom'
import { Button, Container, Spinner, Label, Input } from 'reactstrap'
import { CustomAlert } from '../../components/CustomAlert/CustomAlert'
import { File } from '../../components/Exam/File'
import { getTenantByKey } from '../../helpers/helpers'
import { useExam } from '../../hooks/useExam'

export const ExamMaterial = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { id, employeeId, visitId } = useParams();

    const {
        exam,
        examState,
        tenant,
        videoPercentage,
        wasSeen,
        setWasSeen,
        verify,
        verifyCode,
        setVerifyCode,
        reSendCode,
        codeWasResend,
        message,
        getExam,
        sending,
        feedbackComment,
        setFeedbackComment,
        sendExam,
    } = useExam(id, employeeId, visitId);

    const handleStartExam = () => {
        const questionaryPath = employeeId
            ? `/exam/questionary/${id}/employee/${employeeId}`
            : `/exam/questionary/${id}/visit/${visitId}`;
        navigate(questionaryPath, { state: { fromStartButton: true, wasSeen: true } });
    };

    // Estados de carga
    if (tenant.error) {
        return <Navigate to={'/login'} replace />
    }

    if (tenant.isFetching || examState.isFetching || sending) {
        return (
            <div className='position-absolute top-50 start-50'>
                <Spinner size={'xl'} />
            </div>
        )
    }

    // Verificación de código
    if (!verify) {
        return (
            <Container className="themed-container align-content-center" fluid="md" style={{ minHeight: '100vh' }}>
                <div className="row justify-content-center align-items-center">
                    <div className="col-md-6 bg-white p-4 rounded-3 shadow-sm">
                        <div className="text-center mb-4">
                            <img src={getTenantByKey('logo')} alt="" className="mb-4" style={{ maxHeight: '60px' }} />
                            <h4>{t('verification.title')}</h4>
                            <p className="text-muted">
                                {codeWasResend ? t('verification.resend_success') : t('verification.code_sent')}
                            </p>
                        </div>
                        <div className="mb-3">
                            <input
                                type="text"
                                className="form-control"
                                placeholder={t('verification.placeholder')}
                                value={verifyCode}
                                onChange={(e) => setVerifyCode(e.target.value)}
                            />
                        </div>
                        <div className="d-grid gap-2 mb-2">
                            <Button
                                color="primary"
                                onClick={getExam}
                                disabled={!verifyCode}
                            >
                                {t('verification.submit')}
                            </Button>
                        </div>
                        <div className="d-grid gap-2">
                            <Button color="link" onClick={reSendCode}>
                                {t('verification.resend_code')}
                            </Button>
                        </div>
                        {examState.error && <CustomAlert color="danger" text={message} />}
                    </div>
                </div>
            </Container>
        )
    }

    // Si ya está aprobado, redirigir al resultado
    if (verify && exam && exam.viewed === true && exam.approved === true) {
        const resultPath = employeeId 
            ? `/exam/result/${id}/employee/${employeeId}` 
            : `/exam/result/${id}/visit/${visitId}`;
        return <Navigate to={resultPath} replace />
    }

    // Si no puede tomar el examen
    if (verify && exam && !exam.can_take_exam) {
        return (
            <Container className="bg-white pb-5 pt-5  rounded-3" fluid="md">
                <div className="text-center">
                    <img src={getTenantByKey('logo')} alt="" className="mb-4" style={{ maxHeight: '60px' }} />
                    <CustomAlert
                        color="danger"
                        text={exam.induction.failed_attempts_message || t('exam.max_attempts_reached')}
                    />
                </div>
            </Container>
        )
    }

    // Si no hay cuestionario y ya vio todo y envió el feedback, redirigir al resultado
    // (esto se maneja en sendExam, no necesitamos redirigir automáticamente aquí)

    // Vista principal del material
    if (!exam) {
        return null;
    }

    const userName = employeeId 
        ? `${exam.contractor_name} (${exam.contractor_code})`
        : `${exam.visit.first_name} ${exam.visit.last_name}`;

    return (
        <Container className="bg-white pb-5 pt-5 mt-5 rounded-3" fluid="md">
            <div className="row mb-4">
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                    <h4 className="mb-0">{userName}</h4>
                    <img src={getTenantByKey('logo')} alt="" style={{ maxHeight: '50px' }} />
                </div>
            </div>

            <div className="row">
                <div className="col-lg-12">
                    <File
                        induction={exam.induction}
                        haveQuestionary={exam.questionary}
                        videoPercentage={videoPercentage}
                        setWasSeen={setWasSeen}
                    />
                    
                </div>
                       {exam.questionary && (
                <div className="mt-4 text-center">
                    <Button
                        color="primary"
                        size="lg"
                        onClick={handleStartExam}
                        disabled={!wasSeen}
                        style={{ 
                            minWidth: '200px', 
                            fontSize: '1.1rem', 
                            padding: '12px 30px',
                            opacity: wasSeen ? 1 : 0.6,
                            cursor: wasSeen ? 'pointer' : 'not-allowed'
                        }}
                    >
                        Iniciar Examen
                    </Button>
                    {!wasSeen && (
                        <p className="text-muted mt-2 small">
                            {t('exam.must_view_all_pages_to_start_exam')}
                        </p>
                    )}
                </div>
            )}

            {/* Feedback cuando no hay cuestionario */}
            {!exam.questionary && (
                <div className="mt-4">
                    <div className="mb-4">
                        <Label htmlFor="feedback" className="fw-bold">
                            {t('questionary.feedback_label')}
                        </Label>
                        <Input
                            type="textarea"
                            id="feedback"
                            name="feedback"
                            rows={4}
                            placeholder={t('questionary.feedback_placeholder')}
                            value={feedbackComment}
                            onChange={(e) => setFeedbackComment(e.target.value)}
                            style={{ resize: 'vertical' }}
                        />
                    </div>
                    <div className="text-center">
                        <Button
                            color="primary"
                            size="lg"
                            onClick={sendExam}
                            disabled={sending || !wasSeen}
                            style={{ 
                                minWidth: '200px', 
                                fontSize: '1.1rem', 
                                padding: '12px 30px',
                                opacity: wasSeen ? 1 : 0.6,
                                cursor: wasSeen ? 'pointer' : 'not-allowed'
                            }}
                        >
                            {sending ? t('common.sending') || 'Enviando...' : t('common.end')}
                        </Button>
                        {!wasSeen && (
                            <p className="text-muted mt-2 small">
                                {t('exam.must_read_all_pages_to_finish')}
                            </p>
                        )}
                    </div>
                </div>
            )}
             
            </div>

        

            {examState.error && (
                <div className="mt-3">
                    <CustomAlert color="danger" text={examState.message} />
                </div>
            )}
        </Container>
    )
}
